import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import configuredStore from 'common/configStore';
import Auth from '@aws-amplify/auth';

// Module-level in-flight dedupe
let inflightTotpEnrollment = null; // Promise<{ secret, uri }> | null
let inflightMfaPreferenceUpdate = null; // Promise<void> | null

// Internal helpers to dispatch partial state updates
const setTotpState = (dispatch, payload) => dispatch({ type: 'AUTH_TOTP_SET_STATE', payload });

const getAccountName = () => {
  const state = configuredStore.store.getState();
  const user = state?.auth?.user;
  return (user && (user.attributes?.email || user.username)) || 'user';
};

const getIssuer = () => process.env.REACT_APP_TOTP_APP_NAME || 'TrialView';

const buildOtpAuthUri = (issuer, accountName, secret) => {
  const label = `${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}`;
  const params = new URLSearchParams({
    secret,
    issuer,
    algorithm: 'SHA1',
    digits: '6',
    period: '30',
  });
  return `otpauth://totp/${label}?${params.toString()}`;
};

export function useTotpEnrollment() {
  const dispatch = useDispatch();

  const startTotpEnrollment = useCallback(async () => {
    if (inflightTotpEnrollment) return inflightTotpEnrollment;

    setTotpState(dispatch, {
      totpSetupLoading: true,
      totpSetupError: null,
      totpSecret: null,
      totpUri: null,
    });

    inflightTotpEnrollment = (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const sharedSecret = await Auth.setupTOTP(user);
        const issuer = getIssuer();
        const accountName = getAccountName();
        const uri = buildOtpAuthUri(issuer, accountName, sharedSecret);

        setTotpState(dispatch, {
          totpSecret: sharedSecret,
          totpUri: uri,
          totpSetupLoading: false,
          totpSetupError: null,
        });
        return { secret: sharedSecret, uri };
      } catch (err) {
        setTotpState(dispatch, {
          totpSetupLoading: false,
          totpSetupError: 'Failed to generate authenticator key. Please try again.',
        });
        inflightTotpEnrollment = null;
        throw err;
      }
    })();

    return inflightTotpEnrollment;
  }, [dispatch]);

  const clearTotpEnrollment = useCallback(() => {
    inflightTotpEnrollment = null;
    setTotpState(dispatch, {}); // no-op
    dispatch({ type: 'AUTH_TOTP_CLEAR' });
  }, [dispatch]);

  const setPreferredMfa = useCallback(async (method) => {
    // Dedupe to avoid: "Only one request to update user MFA settings can be processed at a time."
    const doUpdate = async () => {
      const user = await Auth.currentAuthenticatedUser();
      switch (method) {
        case 'SMS':
          await Auth.setPreferredMFA(user, 'SMS');
          break;
        case 'TOTP':
          await Auth.setPreferredMFA(user, 'TOTP');
          break;
        case 'EMAIL':
          // Not supported via Cognito user pools client directly
          break;
        case 'NOMFA':
          await Auth.setPreferredMFA(user, 'NOMFA');
          break;
        default:
          break;
      }
    };

    if (inflightMfaPreferenceUpdate) return inflightMfaPreferenceUpdate;
    inflightMfaPreferenceUpdate = doUpdate()
      .catch((e) => {
        inflightMfaPreferenceUpdate = null;
        throw e;
      })
      .then((r) => {
        inflightMfaPreferenceUpdate = null;
        return r;
      });
    return inflightMfaPreferenceUpdate;
  }, []);

  const verifyTotpEnrollment = useCallback(
    async (code) => {
      // No logging of codes
      setTotpState(dispatch, { totpSetupLoading: true, totpSetupError: null });
      try {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.verifyTotpToken(user, code);
        await setPreferredMfa('TOTP');
        // Clear ephemeral TOTP state on success
        dispatch({ type: 'AUTH_TOTP_CLEAR' });
      } catch (err) {
        setTotpState(dispatch, {
          totpSetupLoading: false,
          totpSetupError: 'Invalid code. Double-check and try again.',
        });
        throw err;
      }
    },
    [dispatch, setPreferredMfa],
  );

  return {
    startTotpEnrollment,
    verifyTotpEnrollment,
    clearTotpEnrollment,
    setPreferredMfa,
  };
}

// Export inflight handles for any rare external use/testing
export { inflightTotpEnrollment, inflightMfaPreferenceUpdate };
